import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Megaphone, Check, X, PartyPopper, Inbox } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
};

const Avatar = ({ name, photo }) => (
  photo ? (
    <img src={photo} alt={name} className="w-10 h-10 rounded-xl object-cover flex-shrink-0 ring-1 ring-white/15" />
  ) : (
    <span className="w-10 h-10 rounded-xl flex-shrink-0 grid place-items-center font-extrabold text-black bg-[#39FF14] text-sm">
      {(name || '?').trim().charAt(0).toUpperCase()}
    </span>
  )
);

/**
 * AI-booth guest shoutout box: staff moderate incoming shoutouts (approve /
 * reject) and approved ones pop onto a "LIVE ON SCREEN" stage — tuned to look
 * great when the tablet is cast to a venue TV.
 */
export default function AiShoutoutInbox() {
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/shoutouts/inbox`);
      setPending(r.data?.pending || []);
      setApproved(r.data?.approved || []);
    } catch { /* silent — box just shows empty */ }
  }, []);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 4000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const act = async (id, action) => {
    setBusyId(id);
    try {
      await axios.put(`${API}/shoutouts/${id}/${action}`);
      if (action === 'approve') toast.success('🎉 Shoutout is now on screen!');
      else toast('Shoutout dismissed');
      await load();
    } catch {
      toast.error('Could not update shoutout');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section
      className="relative rounded-3xl overflow-hidden flex flex-col h-full min-h-[360px] bg-[#0E0E16]/80 border-2 border-[#FF007F]/40 shadow-[0_0_30px_-8px_rgba(255,0,127,0.5),inset_0_0_20px_rgba(0,255,255,0.05)]"
      data-testid="shoutout-inbox"
    >
      {/* header marquee */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-black/30">
        <span className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.25em] text-[#FF007F]">
          <Megaphone className="w-4 h-4" /> Guest Shoutouts
        </span>
        <span className="text-[10px] font-mono text-gray-500" data-testid="shoutout-pending-count">
          {pending.length} waiting
        </span>
      </div>

      {/* LIVE approved stage */}
      <div className="px-4 pt-3" data-testid="shoutout-approved-stage">
        <AnimatePresence initial={false}>
          {approved.slice(0, 2).map((s) => (
            <motion.div
              key={s.id}
              initial={{ scale: 0.8, opacity: 0, y: 8 }}
              animate={{ scale: [0.8, 1.08, 1], opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.5, times: [0, 0.6, 1] }}
              className="mb-3 rounded-2xl p-4 bg-gradient-to-br from-[#FF007F]/20 to-[#A855F7]/10 border-2 border-[#FF007F] shadow-[0_0_40px_-8px_#FF007F]"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <PartyPopper className="w-4 h-4 text-[#FFD700]" />
                <span className="text-[10px] font-extrabold uppercase tracking-[0.25em] text-[#39FF14] animate-pulse">Now on screen</span>
              </div>
              <div className="flex items-start gap-3">
                <Avatar name={s.guest_name} photo={s.photo_data_url} />
                <div className="min-w-0">
                  <p className="text-white font-extrabold leading-tight">{s.guest_name}</p>
                  <p className="text-sm text-gray-200 leading-snug break-words">{s.message}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Pending inbox */}
      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2" data-testid="shoutout-pending-list">
        {pending.length === 0 && approved.length === 0 ? (
          <div className="h-full min-h-[160px] grid place-items-center text-center">
            <div className="text-gray-500">
              <Inbox className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No shoutouts yet.</p>
              <p className="text-[11px] text-gray-600 mt-1">Guests can send one from the table QR.</p>
            </div>
          </div>
        ) : (
          pending.map((s) => (
            <div
              key={s.id}
              className="rounded-2xl p-3 flex items-center justify-between gap-3 bg-white/[0.03] border border-white/10 hover:border-[#00FFFF]/50 transition-all"
              data-testid={`shoutout-card-${s.id}`}
            >
              <div className="flex items-start gap-3 min-w-0">
                <Avatar name={s.guest_name} photo={s.photo_data_url} />
                <div className="min-w-0">
                  <p className="text-white font-bold leading-tight truncate">
                    {s.guest_name} <span className="text-gray-600 font-normal text-[10px]">· {timeAgo(s.created_at)}</span>
                  </p>
                  <p className="text-sm text-gray-300 leading-snug break-words">{s.message}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => act(s.id, 'approve')}
                  disabled={busyId === s.id}
                  data-testid={`shoutout-approve-${s.id}`}
                  className="px-3 py-1.5 rounded-xl bg-[#39FF14] text-black font-extrabold text-xs uppercase tracking-wider hover:shadow-[0_0_15px_#39FF14] disabled:opacity-50 transition-all flex items-center gap-1"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => act(s.id, 'reject')}
                  disabled={busyId === s.id}
                  data-testid={`shoutout-reject-${s.id}`}
                  className="px-3 py-1.5 rounded-xl bg-white/10 text-gray-400 font-bold text-xs hover:bg-red-500/20 hover:text-red-400 disabled:opacity-50 transition-all"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
